import { useEffect } from 'react';

import { Button, Icon, Link, Modal, ModalActions, ModalContent, ModalWrapper } from '@braskai/ui-kit';

import type { TrialPlan } from '@/features/pricing/trialPaywall/model/PLANS';

import { Stack, Typography } from '@mui/material';

import { formatBoosterPrice, formatBoosterSeatsText } from '@/features/boosters/lib/formatBoosterPrice';
import { useBoosterConfirmation } from '@/features/boosters/model/useBoosterConfirmation';
import { useBoosters } from '@/features/boosters/model/useBoosters';
import { useBoosterSeatsCounter } from '@/features/boosters/model/useBoosterSeatsCounter';
import { BoosterSeatsCounter } from '@/features/boosters/ui/BoosterSeatsCounter/BoosterSeatsCounter';
import { useTrialPaywallModalStore } from '@/features/pricing/trialPaywall/model/useTrialPaywallModalStore';

import { useBalanceStore } from '@/entities/balance';

import { IS_WHITE_LABEL } from '@/shared/environments';
import { useAppSelector } from '@/shared/hooks-tmp/reduxToolkitHooks';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useOpenPricingPage } from '@/shared/hooks/useOpenPricingPage';
import { usePaywallExperiment } from '@/shared/hooks/usePaywallExperiment';
import { useInterfaceLanguage } from '@/shared/i18n';
import { createTextParser } from '@/shared/lib/createTextParser';
import { selectIsSegmentSavePending } from '@/shared/store-tmp/editor/editorSelectors';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { BoosterType } from '@/shared/types/Billing';
import { FeaturePaywall } from '@/shared/ui/FeaturePaywall';
import { ModalHeaderSecondary } from '@/shared/ui/modal';

import { DATA_TEST_IDS } from '../../lib/constants';
import { getFeaturePaywallContent, getFeaturePaywallVariant } from '../../lib/featurePaywallContent';
import { TRIAL_PAYWALL_TITLES } from '../../lib/limitedFeatures/paywallTitles';
import { BUTTON_TEXTS } from '../../lib/limitedFeatures/texts';
import { translateLimitedFeatureCopy } from '../../lib/limitedFeatures/translateLimitedFeatureCopy';
import { LimitedFeature } from '../../lib/types';
import { useTrialPricingModalStore } from '../../model/useTrialPricingModalStore';
import { useUpgradePlanModalContent } from '../../model/useUpgradePlanModalContent';
import { useUpgradePlanModalStore } from '../../model/useUpgradePlanModalStore';
import { SDescriptionText, SImage, SNativeVideoPlayer } from './styled';

type UpgradePlanModalProps = {
  isSpaceOwner: boolean;
  onContactSales: (sourceText: string) => void;
};

const UpgradePlanModalComponent = ({ isSpaceOwner, onContactSales }: UpgradePlanModalProps) => {
  const limitedFeature = useUpgradePlanModalStore((state) => state.limitedFeature);
  const plan = useUpgradePlanModalStore((state) => state.plan);
  const fromTrialPreview = useUpgradePlanModalStore((state) => state.fromTrialPreview);
  const close = useUpgradePlanModalStore((state) => state.close);

  const openPricingPage = useOpenPricingPage();
  const variant = getFeaturePaywallVariant(limitedFeature);
  const experimentVariant = usePaywallExperiment(variant);
  const isNewPaywall = variant && experimentVariant === 'B';
  const isSegmentSavePending = useAppSelector(selectIsSegmentSavePending);
  const downloadSource = useUpgradePlanModalStore((state) => state.downloadSource);
  const pricingEntryPoint = useUpgradePlanModalStore((state) => state.pricingEntryPoint) ?? 'unattributed_paywall';

  const { content, planCategory } = useUpgradePlanModalContent(limitedFeature as LimitedFeature, plan);
  const openTrialPaywall = useTrialPaywallModalStore((state) => state.open);
  const openTrialPricingModal = useTrialPricingModalStore((state) => state.open);
  const { allBoosters, canPurchaseBoosters, purchasedBoosters } = useBoosters();
  const { locale, t } = useInterfaceLanguage();

  const { decrement, increment, seatsCount } = useBoosterSeatsCounter(1);
  const { openBoosterConfirmation } = useBoosterConfirmation();

  const { hasFreeSubscription, hasTrialPlan } = useBalanceStore();
  const hasPreviewPlan = hasTrialPlan || hasFreeSubscription;

  const { checkIsSpaceOwner = true } = content ?? {};

  // Downloads used the plan selector directly before the redesign.
  useEffect(() => {
    if (variant === 'download' && !isNewPaywall) {
      close();
      openTrialPaywall({ pricingEntryPoint });
    }
  }, [variant, isNewPaywall, close, openTrialPaywall, pricingEntryPoint]);

  useExecuteOneTime(() => {
    if (variant === 'download' && !isNewPaywall) return;
    if (limitedFeature === 'download' && downloadSource === 'export') {
      MixpanelEvents.trialExportPaywallShown();
    } else {
      content?.mixpanelOpeningEvent();
    }
  });

  if (!content) {
    console.error('Should show upgrade modal but no content for this plan category was provided');

    return null;
  }

  const {
    title,
    bulletsHeader,
    buttonText,
    caption,
    features,
    media: { poster, src, type: mediaType, videoOptions },
    mixpanelButtonClickEvent,
    subtitle,
  } = content;

  // Redesigned banners reserve a uniform media box so the modal height stays stable across features.
  const isRedesignedBanner = Boolean(content.content);
  let videoSx;
  if (!isRedesignedBanner) {
    videoSx = { margin: '0 auto', maxWidth: hasTrialPlan ? '356px' : '100%' };
  }

  const handleButtonClick = () => {
    if (isNewPaywall && isSegmentSavePending) return;

    if (limitedFeature === 'download' && downloadSource === 'export') {
      MixpanelEvents.trialExportPaywallUnlockClick();
    } else {
      mixpanelButtonClickEvent();
    }

    if (isNewPaywall) {
      close();
      openPricingPage(pricingEntryPoint);
      return;
    }

    if (buttonText === BUTTON_TEXTS.contactUs) {
      onContactSales(buttonText);
      return;
    }

    const selectedPlan = content?.content?.selectedPlan;
    const allowedPlanNames = content?.content?.allowedPlanNames as TrialPlan['name'][] | undefined;
    // A paywall config (even an empty Enterprise-only plan list) routes to the
    // plan-selector paywall with the feature-specific title (Figma 3-Pricing).
    const hasPaywallConfig = allowedPlanNames !== undefined || selectedPlan !== undefined;

    if (hasPaywallConfig) {
      const paywallTitle = TRIAL_PAYWALL_TITLES[limitedFeature as LimitedFeature] ?? content.title;
      openTrialPaywall({
        title: translateLimitedFeatureCopy(t, paywallTitle),
        allowedPlanNames: allowedPlanNames ?? null,
        preSelectedPlanName: (selectedPlan as TrialPlan['name'] | undefined) ?? null,
        pricingEntryPoint,
      });
    } else if (fromTrialPreview) {
      openTrialPricingModal({ openedAutomatically: false, pricingEntryPoint });
    } else {
      openPricingPage(pricingEntryPoint);
    }

    close();
  };

  const handleBuyBoosterClick = () => {
    if (!content?.booster) return;

    MixpanelEvents.boostersAddClick({ source: 'upgrade_banner' });
    close();

    const boosterType = content.booster as BoosterType;
    const existingSeats = purchasedBoosters?.[BoosterType.TeamspaceSeat]?.quantity ?? 0;
    const isAddingSeat = boosterType === BoosterType.TeamspaceSeat;
    const finalSeatsCount = isAddingSeat ? existingSeats + seatsCount : existingSeats;

    openBoosterConfirmation({
      seatsCount: finalSeatsCount,
      selectedBoosters: {
        [BoosterType.SRT]: (purchasedBoosters?.[BoosterType.SRT]?.quantity ?? 0) > 0 || boosterType === BoosterType.SRT,
        [BoosterType.TeamspaceSeat]: finalSeatsCount > 0,
        [BoosterType.TranslationPrompting]:
          (purchasedBoosters?.[BoosterType.TranslationPrompting]?.quantity ?? 0) > 0 ||
          boosterType === BoosterType.TranslationPrompting,
      },
    });
  };

  const parseLinksAndBoldAndGrayText = createTextParser(Link, SDescriptionText);
  const translatedCaption = caption ? translateLimitedFeatureCopy(t, caption) : undefined;

  const boosterAction = !IS_WHITE_LABEL && content?.booster && canPurchaseBoosters && (
    <Stack flexDirection="row" gap={8}>
      {content?.booster === BoosterType.TeamspaceSeat && (
        <BoosterSeatsCounter
          seatsCount={seatsCount}
          variant="bordered"
          onDecrement={decrement}
          onIncrement={increment}
        />
      )}
      <Button
        data-testid="upgrade-plan-modal-buy-booster-click"
        size="lg"
        variant="outlined"
        fullWidth
        onClick={handleBuyBoosterClick}
      >
        {content.booster === BoosterType.TeamspaceSeat
          ? formatBoosterSeatsText(seatsCount, allBoosters, locale, t)
          : t('pricing.booster.addForMonth', {
              price: formatBoosterPrice(allBoosters, content.booster, seatsCount, locale),
            })}
      </Button>
    </Stack>
  );

  if (variant === 'download' && !isNewPaywall) return null;

  if (isNewPaywall) {
    const design = getFeaturePaywallContent(t)[variant];
    const isPaid = planCategory !== 'trial';
    const paidBullets = content.content?.whatYouCanDo.bullets ?? features.map((feature) => feature.description);
    const preservePaidCopy = isPaid && variant !== 'download' && variant !== 'unlimitedLength';
    const enhancedLipSync =
      variant === 'lipSync' && !preservePaidCopy
        ? content.content?.whatYouCanDo.bullets.find((bullet) => bullet.startsWith('New Enhanced lip-sync model'))
        : undefined;
    let { badge } = design;
    if (variant === 'voicePresets' && planCategory === 'creatorPro') badge = t('pricing.featurePaywall.badge.business');
    if (variant === 'voicePresets' && planCategory === 'business') badge = t('pricing.featurePaywall.badge.enterprise');
    const bullets = preservePaidCopy
      ? paidBullets.map((bullet) => ({
          id: bullet,
          content: parseLinksAndBoldAndGrayText(translateLimitedFeatureCopy(t, bullet)),
        }))
      : design.bullets.map((bullet) => ({ id: bullet, content: bullet }));
    let description = preservePaidCopy ? translateLimitedFeatureCopy(t, subtitle) : design.subtitle;
    if (variant === 'download' && !hasPreviewPlan) description = t('pricing.featurePaywall.download.paidSubtitle');
    if (variant === 'unlimitedLength' && isPaid) description = t('pricing.featurePaywall.unlimitedLength.paidSubtitle');

    let displayTitle = preservePaidCopy ? translateLimitedFeatureCopy(t, title) : design.title;
    if (variant === 'download' && !hasPreviewPlan) displayTitle = t('pricing.featurePaywall.download.paidTitle');

    return (
      <FeaturePaywall
        title={displayTitle}
        bullets={
          enhancedLipSync
            ? [...bullets, { id: enhancedLipSync, content: translateLimitedFeatureCopy(t, enhancedLipSync) }]
            : bullets
        }
        badge={badge}
        choosePlanLabel={t('pricing.featurePaywall.choosePlan')}
        closeLabel={t('pricing.featurePaywall.close')}
        disabled={isSegmentSavePending || (checkIsSpaceOwner && !isSpaceOwner)}
        experimentVariant={experimentVariant}
        hideAction={IS_WHITE_LABEL}
        image={design.image}
        note={preservePaidCopy ? translateLimitedFeatureCopy(t, content.content?.perfectFor?.text ?? '') : design.note}
        secondaryAction={boosterAction}
        subtitle={description}
        videoSrc={variant === 'lipSync' && mediaType === 'video' ? src : undefined}
        onChoosePlan={handleButtonClick}
        onClose={close}
      />
    );
  }

  return (
    <Modal data-paywall-experiment-variant={variant ? experimentVariant : undefined} size="xs" open onClose={close}>
      <ModalHeaderSecondary
        title={translateLimitedFeatureCopy(t, title)}
        subtitle={translateLimitedFeatureCopy(t, subtitle)}
        onClose={close}
      />

      <ModalContent noPadding>
        {mediaType === 'video' ? (
          <SNativeVideoPlayer $isCover={isRedesignedBanner} poster={poster} src={src} sx={videoSx} {...videoOptions} />
        ) : (
          <SImage $isCover={isRedesignedBanner} $isGif={mediaType === 'gif'} src={src} />
        )}

        {(features?.length ?? 0) > 0 && (
          <>
            {translatedCaption && (
              <Typography color="grey.700" marginTop={8} variant="caption-1-md">
                {parseLinksAndBoldAndGrayText(translatedCaption)}
              </Typography>
            )}

            <Typography color="grey.700" component="h3" marginTop={16} variant="body-1-md">
              {translateLimitedFeatureCopy(t, bulletsHeader)}
            </Typography>

            <Stack gap={12} marginBottom={4} marginTop={12}>
              {features.map(({ description, icon }) => (
                <Stack key={description} direction="row" gap={8}>
                  <Icon color="primary.600" component={icon} size={20} />
                  <Typography color="grey.700" variant="body-2-md">
                    {parseLinksAndBoldAndGrayText(translateLimitedFeatureCopy(t, description))}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
        {content?.content && (
          <Stack marginTop={20}>
            <Typography color="grey.900" variant="body-2-md">
              {translateLimitedFeatureCopy(t, content.content.whatYouCanDo.title)}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {content.content.whatYouCanDo.bullets.map((bullet) => (
                <li key={bullet}>
                  <Typography color="grey.700" variant="body-2">
                    {translateLimitedFeatureCopy(t, bullet)}
                  </Typography>
                </li>
              ))}
            </ul>
            {content.content.learnMore && (
              <Typography color="grey.700" marginTop={12} variant="body-2">
                <Link href={content.content.learnMore.link} rel="noreferrer noopener" size="md" target="_blank">
                  {translateLimitedFeatureCopy(t, content.content.learnMore.title)}
                </Link>{' '}
                <span style={{ fontWeight: 500 }}>
                  {translateLimitedFeatureCopy(t, content.content.learnMore.text)}
                </span>{' '}
              </Typography>
            )}
            {content.content.perfectFor && (
              <Stack
                sx={{
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 12,
                  marginTop: 12,
                  padding: 16,
                }}
              >
                <Typography color="grey.700" variant="body-2">
                  <Typography color="grey.900" component="span" variant="body-2-md">
                    {translateLimitedFeatureCopy(t, content.content.perfectFor.title)}
                  </Typography>{' '}
                  {translateLimitedFeatureCopy(t, content.content.perfectFor.text)}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </ModalContent>

      <ModalActions>
        <Stack direction="column" gap={12} width="100%">
          {boosterAction}

          {!IS_WHITE_LABEL && (
            <Button
              data-testid={DATA_TEST_IDS.upgradeButton}
              disabled={checkIsSpaceOwner && !isSpaceOwner}
              size="lg"
              variant="primary"
              fullWidth
              onClick={handleButtonClick}
            >
              {translateLimitedFeatureCopy(t, buttonText)}
            </Button>
          )}
        </Stack>
      </ModalActions>
    </Modal>
  );
};

export const UpgradePlanModal = ({ isSpaceOwner, onContactSales }: UpgradePlanModalProps) => (
  <ModalWrapper hook={useUpgradePlanModalStore}>
    <UpgradePlanModalComponent isSpaceOwner={isSpaceOwner} onContactSales={onContactSales} />
  </ModalWrapper>
);
